import React from 'react';
import { useTranslation } from 'react-i18next';

// Common timezones grouped by region
const TIMEZONE_GROUPS = {
  'Americas': [
    { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
    { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
    { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
    { value: 'America/Phoenix', label: 'Arizona' },
    { value: 'America/Anchorage', label: 'Alaska' },
    { value: 'Pacific/Honolulu', label: 'Hawaii' },
    { value: 'America/Toronto', label: 'Toronto' },
    { value: 'America/Vancouver', label: 'Vancouver' },
    { value: 'America/Mexico_City', label: 'Mexico City' },
    { value: 'America/Monterrey', label: 'Monterrey' },
    { value: 'America/Guatemala', label: 'Central America' },
    { value: 'America/Bogota', label: 'Bogota, Lima' },
    { value: 'America/Caracas', label: 'Caracas' },
    { value: 'America/Santiago', label: 'Santiago' },
    { value: 'America/La_Paz', label: 'La Paz' },
    { value: 'America/Buenos_Aires', label: 'Buenos Aires' },
    { value: 'America/Montevideo', label: 'Montevideo' },
    { value: 'America/Sao_Paulo', label: 'Brasilia' },
  ],
  'Europe': [
    { value: 'Europe/London', label: 'London' },
    { value: 'Europe/Dublin', label: 'Dublin' },
    { value: 'Europe/Lisbon', label: 'Lisbon' },
    { value: 'Europe/Madrid', label: 'Madrid' },
    { value: 'Europe/Barcelona', label: 'Barcelona' },
    { value: 'Europe/Paris', label: 'Paris' },
    { value: 'Europe/Berlin', label: 'Berlin' },
    { value: 'Europe/Amsterdam', label: 'Amsterdam' },
    { value: 'Europe/Brussels', label: 'Brussels' },
    { value: 'Europe/Rome', label: 'Rome' },
    { value: 'Europe/Vienna', label: 'Vienna' },
    { value: 'Europe/Stockholm', label: 'Stockholm' },
    { value: 'Europe/Oslo', label: 'Oslo' },
    { value: 'Europe/Copenhagen', label: 'Copenhagen' },
    { value: 'Europe/Helsinki', label: 'Helsinki' },
    { value: 'Europe/Athens', label: 'Athens' },
    { value: 'Europe/Istanbul', label: 'Istanbul' },
    { value: 'Europe/Moscow', label: 'Moscow' },
  ],
  'Asia/Pacific': [
    { value: 'Asia/Dubai', label: 'Dubai' },
    { value: 'Asia/Jerusalem', label: 'Jerusalem' },
    { value: 'Asia/Kuwait', label: 'Kuwait, Riyadh' },
    { value: 'Asia/Tehran', label: 'Tehran' },
    { value: 'Asia/Karachi', label: 'Karachi' },
    { value: 'Asia/Kolkata', label: 'Mumbai, Delhi' },
    { value: 'Asia/Dhaka', label: 'Dhaka' },
    { value: 'Asia/Bangkok', label: 'Bangkok' },
    { value: 'Asia/Singapore', label: 'Singapore' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
    { value: 'Asia/Shanghai', label: 'Beijing, Shanghai' },
    { value: 'Asia/Taipei', label: 'Taipei' },
    { value: 'Asia/Seoul', label: 'Seoul' },
    { value: 'Asia/Tokyo', label: 'Tokyo' },
    { value: 'Australia/Perth', label: 'Perth' },
    { value: 'Australia/Adelaide', label: 'Adelaide' },
    { value: 'Australia/Brisbane', label: 'Brisbane' },
    { value: 'Australia/Sydney', label: 'Sydney' },
    { value: 'Australia/Melbourne', label: 'Melbourne' },
    { value: 'Pacific/Auckland', label: 'Auckland' },
    { value: 'Pacific/Fiji', label: 'Fiji' },
  ],
  'Africa': [
    { value: 'Africa/Cairo', label: 'Cairo' },
    { value: 'Africa/Johannesburg', label: 'Johannesburg' },
    { value: 'Africa/Lagos', label: 'West Africa Time' },
    { value: 'Africa/Nairobi', label: 'Nairobi' },
    { value: 'Africa/Casablanca', label: 'Casablanca' },
  ],
  'Other': [
    { value: 'UTC', label: 'UTC' },
    { value: 'Atlantic/Reykjavik', label: 'Reykjavik' },
  ]
};

const TimezoneSelector = ({ value, onChange, disabled }) => {
  const { t } = useTranslation(['profile', 'common']);

  // Get current offset for each timezone
  const getTimezoneOffset = (timezone) => {
    try {
      const date = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(date);
      const offsetPart = parts.find(p => p.type === 'timeZoneName');
      return offsetPart ? offsetPart.value : '';
    } catch (e) {
      return '';
    }
  };

  // Detect user's current timezone
  const detectTimezone = () => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      onChange(timezone);
    } catch (e) {
      console.error('Could not detect timezone:', e);
    }
  };

  return (
    <div className="timezone-selector">
      <label htmlFor="timezone" className="form-label">
        {t('common:labels.timezone')}
      </label>
      <div className="timezone-controls">
        <select
          id="timezone"
          name="timezone"
          value={value || 'UTC'}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="form-select"
        >
          {Object.entries(TIMEZONE_GROUPS).map(([region, timezones]) => (
            <optgroup key={region} label={region}>
              {timezones.map(tz => (
                <option key={tz.value} value={tz.value}>
                  {tz.label} ({getTimezoneOffset(tz.value)})
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <button
          type="button"
          onClick={detectTimezone}
          disabled={disabled}
          className="btn btn-secondary btn-sm"
          title={t('profile:timezone.detect_tooltip')}
        >
          {t('profile:timezone.detect_button')}
        </button>
      </div>
    </div>
  );
};

export default TimezoneSelector;